import React, { useState, useEffect } from 'react';
import {
  Ticket,
  Plus,
  Search,
  Filter,
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare,
  History,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Building,
  Wrench,
  Tag,
  Calendar
} from 'lucide-react';
import api from '../services/api';

const PRIORITY_CONFIG = {
  critica: { label: 'Critica', color: 'bg-red-600', textColor: 'text-red-600', bgLight: 'bg-red-50', border: 'border-red-500' },
  alta: { label: 'Alta', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50', border: 'border-orange-500' },
  media: { label: 'Media', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50', border: 'border-yellow-500' },
  baja: { label: 'Baja', color: 'bg-green-500', textColor: 'text-green-600', bgLight: 'bg-green-50', border: 'border-green-500' }
};

const STATUS_CONFIG = {
  abierto: { label: 'Abierto', color: 'bg-blue-500', icon: Ticket },
  asignado: { label: 'Asignado', color: 'bg-purple-500', icon: User },
  en_progreso: { label: 'En Progreso', color: 'bg-yellow-500', icon: Clock },
  pendiente_cliente: { label: 'Pend. Cliente', color: 'bg-orange-500', icon: AlertTriangle },
  resuelto: { label: 'Resuelto', color: 'bg-green-500', icon: CheckCircle },
  cerrado: { label: 'Cerrado', color: 'bg-gray-500', icon: XCircle }
};

const CATEGORY_CONFIG = {
  hardware: { label: 'Hardware', icon: '🖥️' },
  software: { label: 'Software', icon: '💿' },
  red: { label: 'Red', icon: '🌐' },
  acceso: { label: 'Acceso', icon: '🔑' },
  instalacion: { label: 'Instalacion', icon: '📦' },
  mantenimiento: { label: 'Mantenimiento', icon: '🔧' },
  general: { label: 'General', icon: '📋' }
};

const SOURCE_CONFIG = {
  aparato: { label: 'Aparato', icon: '🖥️', color: 'bg-indigo-100 text-indigo-700' },
  chat: { label: 'Chat', icon: '💬', color: 'bg-blue-100 text-blue-700' },
  whatsapp: { label: 'WhatsApp', icon: '📱', color: 'bg-green-100 text-green-700' },
  telefono: { label: 'Telefono', icon: '📞', color: 'bg-orange-100 text-orange-700' },
  email: { label: 'Email', icon: '📧', color: 'bg-purple-100 text-purple-700' },
  manual: { label: 'Manual', icon: '✍️', color: 'bg-gray-100 text-gray-700' }
};

export default function TicketsManager({ user }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    source: '',
    search: ''
  });

  // Estado para nuevo ticket
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 'media',
    source: 'manual',
    client_name: '',
    aparato_info: ''
  });

  // Estado para nuevo comentario
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [filters.status, filters.priority, filters.category, filters.source]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category) params.append('category', filters.category);
      if (filters.source) params.append('source', filters.source);

      const response = await api.get(`/api/tickets?${params.toString()}`);
      let ticketData = response.data.tickets || [];

      // Filtrar por busqueda
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        ticketData = ticketData.filter(t =>
          t.title?.toLowerCase().includes(searchLower) ||
          t.description?.toLowerCase().includes(searchLower) ||
          t.client_name?.toLowerCase().includes(searchLower)
        );
      }

      setTickets(ticketData);
    } catch (error) {
      console.error('Error cargando tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/api/tickets/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error cargando estadisticas:', error);
    }
  };

  const loadTicketDetails = async (ticketId) => {
    try {
      const response = await api.get(`/api/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('Error cargando detalles:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/tickets', newTicket);
      setShowNewTicket(false);
      setNewTicket({
        title: '',
        description: '',
        category: 'general',
        priority: 'media',
        source: 'manual',
        client_name: '',
        aparato_info: ''
      });
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('Error creando ticket:', error);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await api.put(`/api/tickets/${ticketId}`, { status: newStatus });
      loadTickets();
      loadStats();
      if (selectedTicket?.ticket?.id === ticketId) {
        loadTicketDetails(ticketId);
      }
    } catch (error) {
      console.error('Error actualizando estado:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTicket) return;

    try {
      setCommentLoading(true);
      await api.post(`/api/tickets/${selectedTicket.ticket.id}/comments`, {
        content: newComment
      });
      setNewComment('');
      loadTicketDetails(selectedTicket.ticket.id);
    } catch (error) {
      console.error('Error agregando comentario:', error);
    } finally {
      setCommentLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `hace ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `hace ${days}d`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header con estadisticas */}
      <div className="p-4 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Ticket className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold">Gestion de Incidencias</h1>
          </div>
          <button
            onClick={() => setShowNewTicket(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Nueva Incidencia
          </button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{stats.by_status.abierto || 0}</div>
              <div className="text-xs text-gray-600">Abiertas</div>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{stats.by_status.en_progreso || 0}</div>
              <div className="text-xs text-gray-600">En Progreso</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{stats.by_priority.critica || 0}</div>
              <div className="text-xs text-gray-600">Criticas</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{stats.by_status.resuelto || 0}</div>
              <div className="text-xs text-gray-600">Resueltas</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">{stats.total || 0}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{stats.avg_resolution_time || '-'}m</div>
              <div className="text-xs text-gray-600">Tiempo Medio</div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar incidencias..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyUp={(e) => e.key === 'Enter' && loadTickets()}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            <option value="">Todas las prioridades</option>
            {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            <option value="">Todas las categorias</option>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.icon} {config.label}</option>
            ))}
          </select>

          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            <option value="">Todos los origenes</option>
            {Object.entries(SOURCE_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.icon} {config.label}</option>
            ))}
          </select>

          <button
            onClick={loadTickets}
            className="p-2 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Lista de tickets */}
        <div className={`${selectedTicket ? 'w-1/2' : 'w-full'} border-r overflow-y-auto`}>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500">
              <Ticket className="w-10 h-10 mb-2 opacity-50" />
              <p>No hay incidencias</p>
            </div>
          ) : (
            <div className="divide-y">
              {tickets.map(ticket => {
                const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.media;
                const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.abierto;
                const category = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.general;
                const StatusIcon = status.icon;

                return (
                  <div
                    key={ticket.id}
                    onClick={() => loadTicketDetails(ticket.id)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedTicket?.ticket?.id === ticket.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-1 h-full rounded ${priority.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400">#{ticket.ticket_number}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${status.color} text-white`}>
                            {status.label}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${priority.bgLight} ${priority.textColor}`}>
                            {priority.label}
                          </span>
                        </div>
                        <h3 className="font-medium text-gray-900 truncate">{ticket.title}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>{category.icon} {category.label}</span>
                          {ticket.source && SOURCE_CONFIG[ticket.source] && (
                            <span className={`px-1.5 py-0.5 rounded text-xs ${SOURCE_CONFIG[ticket.source].color}`}>
                              {SOURCE_CONFIG[ticket.source].icon} {SOURCE_CONFIG[ticket.source].label}
                            </span>
                          )}
                          {ticket.client_name && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {ticket.client_name}
                            </span>
                          )}
                          {ticket.assigned_to_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.assigned_to_name}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {getTimeAgo(ticket.created_at)} • {formatDate(ticket.created_at)}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detalle del ticket */}
        {selectedTicket && (
          <div className="w-1/2 flex flex-col overflow-hidden bg-white">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">#{selectedTicket.ticket.ticket_number}</span>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <h2 className="text-lg font-bold">{selectedTicket.ticket.title}</h2>

              <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => handleUpdateStatus(selectedTicket.ticket.id, key)}
                    className={`px-3 py-1 text-xs rounded-full ${
                      selectedTicket.ticket.status === key
                        ? `${config.color} text-white`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Descripcion */}
              {selectedTicket.ticket.description && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-medium mb-1">Descripcion</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTicket.ticket.description}
                  </p>
                </div>
              )}

              {/* Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Cliente:</span>
                  <span className="ml-2">{selectedTicket.ticket.client_name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Aparato:</span>
                  <span className="ml-2">{selectedTicket.ticket.aparato_info || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Asignado:</span>
                  <span className="ml-2">{selectedTicket.ticket.assigned_to_name || 'Sin asignar'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Creado:</span>
                  <span className="ml-2">{formatDate(selectedTicket.ticket.created_at)}</span>
                </div>
              </div>

              {/* Comentarios */}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comentarios ({selectedTicket.comments?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedTicket.comments?.map(comment => (
                    <div key={comment.id} className={`p-3 rounded-lg ${
                      comment.is_internal ? 'bg-yellow-50 border-l-4 border-yellow-400' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{comment.user_name}</span>
                        <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                  ))}
                </div>

                {/* Nuevo comentario */}
                <form onSubmit={handleAddComment} className="mt-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Agregar comentario..."
                    className="w-full p-2 border rounded-lg text-sm resize-none"
                    rows="2"
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || commentLoading}
                    className="mt-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {commentLoading ? 'Enviando...' : 'Agregar'}
                  </button>
                </form>
              </div>

              {/* Historial */}
              {selectedTicket.history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Historial
                  </h4>
                  <div className="space-y-1">
                    {selectedTicket.history.map(entry => (
                      <div key={entry.id} className="text-xs text-gray-500 flex items-center gap-2">
                        <span className="w-24">{formatDate(entry.created_at).split(',')[0]}</span>
                        <span className="flex-1">{entry.user_name} - {entry.action}</span>
                        {entry.new_value && <span className="text-gray-700">{entry.new_value}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal nuevo ticket */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold">Nueva Incidencia</h3>
              <button onClick={() => setShowNewTicket(false)} className="p-1 hover:bg-gray-100 rounded">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titulo *</label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descripcion</label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoria</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                  >
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.icon} {config.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Prioridad</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                  >
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Origen</label>
                  <select
                    value={newTicket.source}
                    onChange={(e) => setNewTicket({ ...newTicket, source: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                  >
                    {Object.entries(SOURCE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.icon} {config.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Cliente / Lugar</label>
                <input
                  type="text"
                  value={newTicket.client_name}
                  onChange={(e) => setNewTicket({ ...newTicket, client_name: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Nombre del cliente o lugar"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Aparato / Equipo</label>
                <input
                  type="text"
                  value={newTicket.aparato_info}
                  onChange={(e) => setNewTicket({ ...newTicket, aparato_info: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Modelo, serial, etc."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewTicket(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Crear Incidencia
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
